import React from 'react';
import Box from 'src/components-v2/Layout/Box/Box';
import Stack from 'threads5/Stack';
import styled from 'threads5/themes/styled';
import Icon from 'threads5/Icon';
import { Image } from 'src/components-v2/Media';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';

interface Props {
  title: string;
  subtitle: string;
  description: string;
  keyFeatures: string[];
  color: string;
  ctaText: string;
  buttonSx?: SxProps<ITheme>;
  href: string;
}

const StyledWrapper = styled(Box)(({ theme }) => {
  return {
    maxWidth: '441px',
    minHeight: '584px',
    position: 'relative',

    [theme.breakpoints.down(1728)]: {
      minHeight: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  };
});

const StyledCard = styled(Stack)(({ theme }) => {
  return {
    padding: '40px 36px 36px 36px',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    background: '#FFFFFF',
    borderRadius: '12px',
    position: 'relative',
    zIndex: 1,
    // [theme.breakpoints.down(1728)]: {
    //   padding: '16px 20px',
    // },
    [theme.breakpoints.down('md')]: {
      padding: '30px 24px',
    },
  };
});

const StyledCardBody = styled(Box)(({ theme }) => {
  return {
    paddingBottom: '50px',

    [theme.breakpoints.down(1728)]: {
      paddingBottom: '34px',
    },
    [theme.breakpoints.down('lg')]: {
      paddingBottom: '40px',
      minHeight: '336px',
    },
  };
});

const Heading3 = styled('h3')(({ theme }) => {
  return {
    fontFamily: theme.typography.plaidSans,
    fontSize: '4.0rem',
    fontWeight: 600,
    lineHeight: '120%',
    letterSpacing: '-2px',
    textAlign: 'left',
    marginBottom: '8px',
    color: 'var(--Blue-Chip-1000, #00172E)',
    [theme.breakpoints.down('lg')]: {
      fontSize: '2.4rem',
      lineHeight: '122%',
      letterSpacing: '-1px',
    },
  };
});

const Subtitle = styled('p')(({ theme }) => {
  return {
    fontSize: '1.8rem',
    fontWeight: 600,
    lineHeight: '150%',
    letterSpacing: '-0.27px',
    marginBottom: '12px',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.6rem',
      letterSpacing: 'none',
    },
  };
});

const Description = styled('p')(({ theme }) => {
  return {
    fontSize: '2.0rem',
    fontWeight: 400,
    lineHeight: '150%',
    marginBottom: '34px',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.8rem',
    },
  };
});

const KeyFeaturesHeading = styled('p')(({ theme }) => {
  return {
    fontSize: '1.6rem',
    fontWeight: 700,
    lineHeight: '112%',
    letterSpacing: '2px',
    marginBottom: '21px',
    textTransform: 'uppercase',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.4rem',
      letterSpacing: '1.25px',
    },
  };
});

const ButtonLink = styled('a')(({ theme }) => {
  return {
    display: 'inline-flex',
    padding: '8px 32px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '46px',
    color: '#FFF',
    '&:hover, &:focus': {
      color: '#FFF',
    },
    transition: 'background 0.3s ease',
    textAlign: 'center',
    fontFamily: theme.typography.sans,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '150%' /* 30px */,
  };
});

const KeyFeaturesListItemText = styled('p')(({ theme }) => {
  return {
    fontFamily: theme.typography.sans,
    fontSize: '1.8rem',
    fontWeight: 400,
    lineHeight: '150%',
    color: 'var(--Blue-Chip-800, #043C67)',
    marginBottom: 0,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.6rem',
    },
  };
});

const KeyFeaturesListItem = ({
  color,
  text,
}: {
  color: string;
  text: string;
}) => {
  return (
    <Stack
      direction='row'
      gap={1.5}
      component='li'
      sx={{
        listStyleType: 'none',
        marginBottom: '8px',
        '&:last-child': {
          marginBottom: 0,
        },
        alignItems: 'center',
        i: {
          width: '25px',
          height: '25px',
        },
        svg: {
          width: '25px',
          height: '25px',
        },
      }}
    >
      <Icon name='StepComplete' size='20' color={color} />
      <KeyFeaturesListItemText>{text}</KeyFeaturesListItemText>
    </Stack>
  );
};

const PlanCard: React.FC<Props> = ({
  title,
  subtitle,
  description,
  keyFeatures,
  color,
  ctaText,
  buttonSx,
  href,
}) => {
  return (
    <StyledWrapper>
      <Image
        className='hide-for-aeo'
        src='https://images.ctfassets.net/ss5kfr270og3/7rntlKZf5MHgQ1njpMygO/e2e561b8b919420e6bd9f4f1989af275/AdobeStock_566188450.png?fm=webp&q=1'
        alt='rainbowgradient'
        width='400'
        height='400'
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          filter: 'blur(40px)',
          transform: 'translateZ(0)',
          scale: 1.15,
          zIndex: 0,
        }}
      />
      <StyledCard>
        <StyledCardBody>
          <Heading3>{title}</Heading3>
          <Subtitle
            sx={{
              color,
            }}
          >
            {subtitle}
          </Subtitle>
          <Description>{description}</Description>
          <KeyFeaturesHeading>Key Features</KeyFeaturesHeading>
          <ul
            style={{
              margin: 0,
            }}
          >
            {keyFeatures.map((feature, index) => {
              return (
                <KeyFeaturesListItem color={color} text={feature} key={index} />
              );
            })}
          </ul>
        </StyledCardBody>
        <ButtonLink href={href} sx={buttonSx}>
          {ctaText}
        </ButtonLink>
      </StyledCard>
    </StyledWrapper>
  );
};

export default PlanCard;
